import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import GameSection from '../components/GameSection';
import GameCard from '../components/GameCard';
import { gameCategories, getFeaturedGames, getTrendingGames } from '../data/games';
import { Game } from '../types/game';
import './Home.css';

interface HomeLocationState {
  playGameId?: string;
}

const Home: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [favorites, setFavorites] = useState<Set<string>>(new Set());

  useEffect(() => {
    const savedFavorites = JSON.parse(localStorage.getItem('favorites') || '[]');
    setFavorites(new Set(savedFavorites));
  }, []);

  useEffect(() => {
    const state = location.state as HomeLocationState | null;
    if (state?.playGameId) {
      navigate(`/play/${state.playGameId}`, { replace: true, state: null });
    }
  }, [location.state, navigate]);

  const handleFavoriteToggle = (gameId: string) => {
    setFavorites((prev) => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(gameId)) {
        newFavorites.delete(gameId);
      } else {
        newFavorites.add(gameId);
      }
      // Save to localStorage
      localStorage.setItem('favorites', JSON.stringify(Array.from(newFavorites)));
      return newFavorites;
    });
  };

  const getGamesWithFavorites = (games: Game[]): Game[] => {
    return games.map((game) => ({
      ...game,
      isFavorite: favorites.has(game.id),
    }));
  };

  const featuredGames = getGamesWithFavorites(getFeaturedGames());
  const trendingGames = getGamesWithFavorites(getTrendingGames());

  return (
    <div className="home-page">
      {/* Featured Games */}
      {featuredGames.length > 0 && (
        <section className="featured-section">
          <div className="section-header">
            <div className="section-title-container">
              <span className="section-badge featured">⭐ FEATURED</span>
              <h2 className="section-title">Featured Games</h2>
            </div>
          </div>
          <div className="games-grid featured-grid">
            {featuredGames.map((game) => (
              <GameCard
                key={game.id}
                game={game}
                onFavoriteToggle={handleFavoriteToggle}
              />
            ))}
          </div>
        </section>
      )}

      {/* Trending Games */}
      {trendingGames.length > 0 && (
        <section className="trending-section">
          <div className="section-header">
            <div className="section-title-container">
              <span className="section-icon">🔥</span>
              <h2 className="section-title">Trending Now</h2>
            </div>
          </div>
          <div className="games-grid">
            {trendingGames.map((game) => (
              <GameCard
                key={game.id}
                game={game}
                onFavoriteToggle={handleFavoriteToggle}
              />
            ))}
          </div>
        </section>
      )}

      {/* Game Categories */}
      {gameCategories.map((category) => (
        <GameSection
          key={category.id}
          title={category.name}
          games={getGamesWithFavorites(category.games)}
          badge={category.id === 'new-releases' ? 'NEW' : undefined}
          icon={
            category.id === 'arcade'
              ? '🕹️'
              : category.id === 'action'
              ? '👊'
              : undefined
          }
          onFavoriteToggle={handleFavoriteToggle}
        />
      ))}
    </div>
  );
};

export default Home;

